/**
 * ConnectorsTab — Shows integration status for connected systems.
 *
 * Groups connectors by category (ERP, CRM, Communication, Data).
 * Each connector shows its name, description, and connection status.
 *
 * Status is determined by checking environment-level availability
 * rather than runtime API calls — keeps the sidebar fast.
 */

import { useState, useEffect } from 'react'

const CONNECTORS = [
  {
    category: 'ERP',
    items: [
      {
        name: 'Epicor Kinetic',
        icon: '⚡',
        description: 'Full ERP access — 259 agents across 28 modules',
        slug: 'epicor',
        checkEndpoint: '/api/health', // Simple health check
      },
    ],
  },
  {
    category: 'CRM',
    items: [
      {
        name: 'HubSpot',
        icon: '🟠',
        description: 'Companies, contacts, deals, tickets',
        slug: 'hubspot',
        checkEndpoint: null, // No health endpoint; always show as available
        status: 'available',
      },
    ],
  },
  {
    category: 'Communication',
    items: [
      {
        name: 'Microsoft 365',
        icon: '📧',
        description: 'Outlook email, calendar, Teams, SharePoint',
        slug: 'm365',
        checkEndpoint: null,
        status: 'available',
      },
    ],
  },
  {
    category: 'Data',
    items: [
      {
        name: 'CData Connect AI',
        icon: '🔌',
        description: 'SQL access to 200+ SaaS connectors',
        slug: 'cdata',
        checkEndpoint: null,
        status: 'available',
      },
    ],
  },
]

const STATUS_CONFIG = {
  connected: {
    label: 'Connected',
    className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
    dot: 'bg-green-500',
  },
  available: {
    label: 'Available',
    className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    dot: 'bg-blue-500',
  },
  disconnected: {
    label: 'Disconnected',
    className: 'bg-gray-100 dark:bg-gray-800/30 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700',
    dot: 'bg-gray-400',
  },
  error: {
    label: 'Error',
    className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
    dot: 'bg-red-500',
  },
}

export default function ConnectorsTab() {
  const [statuses, setStatuses] = useState({})

  // Check Epicor connectivity on mount
  useEffect(() => {
    async function checkEpicor() {
      try {
        const res = await fetch('/api/health', { method: 'GET' })
        if (res.ok) {
          const data = await res.json()
          setStatuses(prev => ({
            ...prev,
            epicor: data?.epicor?.status === 'connected' ? 'connected' : 'available',
          }))
        } else {
          setStatuses(prev => ({ ...prev, epicor: 'disconnected' }))
        }
      } catch {
        setStatuses(prev => ({ ...prev, epicor: 'available' }))
      }
    }
    checkEpicor()
  }, [])

  const getStatus = (connector) => {
    if (statuses[connector.slug]) return statuses[connector.slug]
    return connector.status || 'available'
  }

  return (
    <div className="p-3 flex flex-col gap-1">
      {CONNECTORS.map((category) => (
        <div key={category.category}>
          <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-1 py-1.5">
            {category.category}
          </div>
          {category.items.map((connector) => {
            const status = getStatus(connector)
            const config = STATUS_CONFIG[status] || STATUS_CONFIG.available

            return (
              <div
                key={connector.slug}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-raised transition-all duration-150 mb-1.5"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[15px] flex-shrink-0 bg-gray-100 dark:bg-surface-dark-overlay">
                  {connector.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                    {connector.name}
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
                    {connector.description}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border flex-shrink-0 ${config.className}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
                  {config.label}
                </span>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
